import type { ChangeBookingFlow } from '@/features/manage/manageActions';
import {
  changeBookingFlowSelector,
  changeTripsSelector,
  manageFlowBookingSelector,
} from '@/features/manage/manageSelectors';
import { useSelector } from '@/store/utils';
import type {
  BookedTripDTO,
  BookingDTO,
  PassengerDTO,
} from '@/types/dto/agent';
import {
  type ALERT_REASON,
  type ChangeTripsSelection,
  detectConflictReason,
  getSelectedAdmissionIds,
  getSelectedPassengerIds,
  getSelectedPassengers,
} from '@/utils/changeTrips';
import { useMemo } from 'react';

/**
 * Single read point for the change-trips selection and everything derived
 * from it against the live booking. Consumers downstream of the
 * Change-Trips modal (New Search, the eventual exchange-operation call)
 * should read from this hook rather than recomposing utils against the
 * booking themselves.
 *
 * All derived fields share one `useMemo` keyed on (booking, selection), so
 * components only re-render when one of those slices actually changes.
 *
 * `selectedOutboundTrip` and `selectedInboundTrip` are exposed separately
 * (rather than as a merged `selectedTrips` array) so consumers can address
 * each direction directly — round-trip-aware prefill, per-direction
 * exchange dispatches, etc. Consumers that want both as an array can
 * merge them locally:
 *   `[selectedOutboundTrip, selectedInboundTrip].filter(Boolean)`
 */
export interface ChangeBookingSelection {
  booking: BookingDTO | null;
  changeFlow: ChangeBookingFlow | null;
  selection: ChangeTripsSelection;
  selectedOutboundTrip: BookedTripDTO | null;
  selectedInboundTrip: BookedTripDTO | null;
  selectedAdmissionIds: Array<string>;
  selectedPassengerIds: Array<string>;
  selectedPassengers: Array<PassengerDTO>;
  /**
   * Why the current selection cannot proceed, or `null` when valid. Run
   * through every validator in `detectConflictReason` — first hit wins.
   * Callers needing a plain boolean can derive `Boolean(conflictReason)`.
   */
  conflictReason: ALERT_REASON | null;
  hasSelection: boolean;
}

export const useChangeBookingSelection = (): ChangeBookingSelection => {
  const booking = useSelector(manageFlowBookingSelector);
  const selection = useSelector(changeTripsSelector);
  const changeFlow = useSelector(changeBookingFlowSelector);

  return useMemo(() => {
    const bookedTrips = booking?.bookedTrips ?? [];
    const passengers = booking?.passengers ?? [];
    const selectedPassengerIds = getSelectedPassengerIds(
      selection,
      bookedTrips
    );
    const selectedOutboundTrip = selection.outboundTripId
      ? (bookedTrips.find(({ id }) => id === selection.outboundTripId) ?? null)
      : null;
    const selectedInboundTrip = selection.returnTripId
      ? (bookedTrips.find(({ id }) => id === selection.returnTripId) ?? null)
      : null;

    return {
      booking,
      selection,
      selectedOutboundTrip,
      selectedInboundTrip,
      selectedAdmissionIds: getSelectedAdmissionIds(selection, bookedTrips),
      selectedPassengerIds,
      selectedPassengers: getSelectedPassengers(
        selection,
        bookedTrips,
        passengers
      ),
      conflictReason: detectConflictReason(selection, bookedTrips),
      hasSelection: Boolean(selection.outboundTripId || selection.returnTripId),
      changeFlow,
    };
  }, [booking, changeFlow, selection]);
};
